import { normalizeSharedConsentSnapshot } from "./shared-consent";
import type { StorageAdapter } from "./storage";
import { postWithRetryResult } from "./transport";
import type { ConsentSnapshot } from "./types";

export type FirstPartyCookieIssuePolicy = {
  region?: string;
  consentMode?: string;
  firstPartyCookies: boolean;
  dataCategories: Record<string, boolean>;
};

export type FirstPartyCookieIssueRequest = {
  siteKey: string;
  action: "issue";
  consent: ConsentSnapshot;
  policy: FirstPartyCookieIssuePolicy;
};

export type FirstPartyCookieProbeRequest = {
  siteKey: string;
  action: "probe";
};

export type FirstPartyConsentPurposeDecisions = {
  analytics: boolean;
  marketing: boolean;
  identity_resolution: boolean;
  enrichment: boolean;
};

export type FirstPartyCookieRevokeRequest = {
  siteKey: string;
  action: "revoke";
  transitionToken: string;
  occurredAt: number;
  consent: ConsentSnapshot;
  policy: FirstPartyCookieIssuePolicy;
};

export type FirstPartyCookieExpireRequest = {
  siteKey: string;
  action: "expire";
};

export type FirstPartyCookieRegrantRequest = {
  siteKey: string;
  action: "regrant";
  transitionVersion: 1;
  transitionToken: string;
  transitionTokens: string[];
  occurredAt: number;
  consent: ConsentSnapshot;
  policy: FirstPartyCookieIssuePolicy;
};

export type FirstPartyConsentTransition = {
  transitionVersion: 1;
  occurredAt: number;
};

export type FirstPartyConsentRevision = {
  transitionToken: string;
  occurredAt: number;
};

const TRANSITION_TOKEN_PATTERN = /^[A-Za-z0-9_-]{22,128}$/;
const TRANSITION_MAX_AGE_MS = 5 * 60 * 1000;
export const MAX_FIRST_PARTY_CONSENT_REVISION_HISTORY = 4;
export const FIRST_PARTY_CONSENT_PURPOSES = [
  "analytics",
  "marketing",
  "identity_resolution",
  "enrichment",
] as const;
export type FirstPartyConsentPurpose =
  (typeof FIRST_PARTY_CONSENT_PURPOSES)[number];

function boundedPolicy(
  policy: FirstPartyCookieIssuePolicy,
): FirstPartyCookieIssuePolicy {
  const dataCategories = Object.fromEntries(
    Object.entries(policy.dataCategories)
      .filter(
        (entry): entry is [string, boolean] =>
          Boolean(entry[0]) && typeof entry[1] === "boolean",
      )
      .slice(0, 64),
  );
  return {
    ...(policy.region ? { region: policy.region.slice(0, 64) } : {}),
    ...(policy.consentMode
      ? { consentMode: policy.consentMode.slice(0, 32) }
      : {}),
    firstPartyCookies: policy.firstPartyCookies === true,
    dataCategories,
  };
}

export function isFirstPartyConsentTransitionToken(value: unknown) {
  return typeof value === "string" && TRANSITION_TOKEN_PATTERN.test(value);
}

function isFirstPartyConsentRevision(
  value: unknown,
): value is FirstPartyConsentRevision {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const candidate = value as Partial<FirstPartyConsentRevision>;
  return (
    isFirstPartyConsentTransitionToken(candidate.transitionToken) &&
    typeof candidate.occurredAt === "number" &&
    Number.isSafeInteger(candidate.occurredAt) &&
    candidate.occurredAt > 0
  );
}

/**
 * Generate an opaque 128-bit consent-revision capability. There is no weak
 * randomness fallback: without Web Crypto the caller must fail closed.
 */
export function createFirstPartyConsentTransitionToken(
  cryptoSource: Pick<Crypto, "getRandomValues"> | undefined = globalThis.crypto,
): string | null {
  if (!cryptoSource?.getRandomValues) return null;
  try {
    const bytes = new Uint8Array(16);
    cryptoSource.getRandomValues(bytes);
    let output = "";
    let buffer = 0;
    let bits = 0;
    const alphabet =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
    for (const byte of bytes) {
      buffer = (buffer << 8) | byte;
      bits += 8;
      while (bits >= 6) {
        bits -= 6;
        output += alphabet[(buffer >>> bits) & 63];
      }
    }
    if (bits > 0) output += alphabet[(buffer << (6 - bits)) & 63];
    return isFirstPartyConsentTransitionToken(output) ? output : null;
  } catch {
    return null;
  }
}

export function firstPartyConsentTransitionTokenStorageKey(
  target: FirstPartyCookieTarget,
) {
  const input = `${target.apiBase.replace(/\/+$/, "")}\n${target.siteKey}`;
  return `prospix_fp_revoke_token_${encodeURIComponent(input)}`;
}

function firstPartyConsentRevisionStoragePrefix(
  target: FirstPartyCookieTarget,
) {
  return `${firstPartyConsentTransitionTokenStorageKey(target)}:`;
}

function firstPartyConsentRevisionStorageKey(
  target: FirstPartyCookieTarget,
  transitionToken: string,
) {
  return `${firstPartyConsentRevisionStoragePrefix(target)}${transitionToken}`;
}

export function readFirstPartyConsentTransitionToken(
  storage: StorageAdapter,
  target: FirstPartyCookieTarget,
) {
  return (
    readFirstPartyConsentRevision(storage, target)?.transitionToken ?? null
  );
}

export function persistFirstPartyConsentTransitionToken(
  storage: StorageAdapter,
  target: FirstPartyCookieTarget,
  token: string,
  occurredAt = Date.now(),
) {
  return persistFirstPartyConsentRevision(storage, target, {
    transitionToken: token,
    occurredAt,
  });
}

export function readFirstPartyConsentRevision(
  storage: StorageAdapter,
  target: FirstPartyCookieTarget,
): FirstPartyConsentRevision | null {
  const key = firstPartyConsentTransitionTokenStorageKey(target);
  const raw = storage.get(key);
  if (raw) {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (isFirstPartyConsentRevision(parsed)) return parsed;
    } catch {
      // A token-only pre-release value has no trustworthy ordering time. It is
      // deliberately not adopted; the runtime rotates a complete revision.
    }
  }
  return readFirstPartyConsentRevisions(storage, target)[0] ?? null;
}

export function readFirstPartyConsentRevisions(
  storage: StorageAdapter,
  target: FirstPartyCookieTarget,
): FirstPartyConsentRevision[] {
  const revisions = new Map<string, FirstPartyConsentRevision>();
  const currentRaw = storage.get(
    firstPartyConsentTransitionTokenStorageKey(target),
  );
  if (currentRaw) {
    try {
      const parsed: unknown = JSON.parse(currentRaw);
      if (isFirstPartyConsentRevision(parsed)) {
        revisions.set(parsed.transitionToken, parsed);
      }
    } catch {
      // Ignore token-only legacy state without an ordering time.
    }
  }
  const prefix = firstPartyConsentRevisionStoragePrefix(target);
  for (const key of storage.keys(prefix)) {
    const raw = storage.get(key);
    if (!raw) continue;
    try {
      const parsed: unknown = JSON.parse(raw);
      if (!isFirstPartyConsentRevision(parsed)) continue;
      const existing = revisions.get(parsed.transitionToken);
      if (!existing || parsed.occurredAt >= existing.occurredAt) {
        revisions.set(parsed.transitionToken, parsed);
      }
    } catch {
      // Ignore a malformed isolated revision without disturbing other tokens.
    }
  }
  return Array.from(revisions.values()).sort(
    (left, right) =>
      right.occurredAt - left.occurredAt ||
      right.transitionToken.localeCompare(left.transitionToken),
  );
}

export function persistFirstPartyConsentRevision(
  storage: StorageAdapter,
  target: FirstPartyCookieTarget,
  revision: FirstPartyConsentRevision,
) {
  if (!isFirstPartyConsentRevision(revision)) return false;
  const existing = readFirstPartyConsentRevisions(storage, target);
  if (
    !existing.some(
      (entry) => entry.transitionToken === revision.transitionToken,
    ) &&
    existing.length >= MAX_FIRST_PARTY_CONSENT_REVISION_HISTORY
  ) {
    // Never evict an unresolved denial capability merely to make room. The
    // runtime stays fail-closed until an acknowledged revision frees space.
    return false;
  }
  const serialized = JSON.stringify(revision);
  storage.set(
    firstPartyConsentRevisionStorageKey(target, revision.transitionToken),
    serialized,
  );
  storage.set(firstPartyConsentTransitionTokenStorageKey(target), serialized);
  const readback = readFirstPartyConsentRevision(storage, target);
  return (
    storage.get(
      firstPartyConsentRevisionStorageKey(target, revision.transitionToken),
    ) === serialized &&
    readback?.transitionToken === revision.transitionToken &&
    readback.occurredAt === revision.occurredAt
  );
}

export function clearFirstPartyConsentTransitionToken(
  storage: StorageAdapter,
  target: FirstPartyCookieTarget,
) {
  storage.remove(firstPartyConsentTransitionTokenStorageKey(target));
  for (const key of storage.keys(
    firstPartyConsentRevisionStoragePrefix(target),
  )) {
    storage.remove(key);
  }
}

export function acknowledgeFirstPartyConsentRevision(
  storage: StorageAdapter,
  target: FirstPartyCookieTarget,
  transitionToken: string,
) {
  if (!isFirstPartyConsentTransitionToken(transitionToken)) return false;
  const current = readFirstPartyConsentRevision(storage, target);
  storage.remove(firstPartyConsentRevisionStorageKey(target, transitionToken));
  if (current?.transitionToken === transitionToken) {
    storage.remove(firstPartyConsentTransitionTokenStorageKey(target));
    const next = readFirstPartyConsentRevisions(storage, target)[0];
    if (next) {
      storage.set(
        firstPartyConsentTransitionTokenStorageKey(target),
        JSON.stringify(next),
      );
    }
  }
  return !readFirstPartyConsentRevisions(storage, target).some(
    (revision) => revision.transitionToken === transitionToken,
  );
}

export function resolveFirstPartyConsentPurposeDecisions(options: {
  analyticsAllowed: boolean;
  marketingAllowed: boolean;
  firstPartyCookies: boolean;
  identityLinkage: boolean;
}): FirstPartyConsentPurposeDecisions {
  const identityAllowed =
    options.marketingAllowed &&
    options.firstPartyCookies &&
    options.identityLinkage;
  return {
    analytics: options.analyticsAllowed,
    marketing: options.marketingAllowed,
    identity_resolution: identityAllowed,
    enrichment: identityAllowed,
  };
}

export function hasFirstPartyPurposeBroadening(
  previous: FirstPartyConsentPurposeDecisions,
  next: FirstPartyConsentPurposeDecisions,
) {
  return (Object.keys(next) as Array<keyof typeof next>).some(
    (purpose) => previous[purpose] !== true && next[purpose] === true,
  );
}

export function hasFirstPartyDeliveryBroadening(
  previous: { analyticsAllowed: boolean; marketingAllowed: boolean },
  next: { analyticsAllowed: boolean; marketingAllowed: boolean },
) {
  return (
    (!previous.analyticsAllowed && next.analyticsAllowed) ||
    (!previous.marketingAllowed && next.marketingAllowed)
  );
}

export function shouldHoldFirstPartyConsentFence(options: {
  analyticsFenced: boolean;
  marketingFenced: boolean;
  freshTransition: boolean;
  analyticsAllowed: boolean;
  marketingAllowed: boolean;
}) {
  return (
    !options.freshTransition &&
    ((options.analyticsFenced && options.analyticsAllowed) ||
      (options.marketingFenced && options.marketingAllowed))
  );
}

export function shouldProbeFirstPartyConsentFence(options: {
  transportVerified: boolean;
  analyticsFenced: boolean;
  marketingFenced: boolean;
  analyticsAllowed: boolean;
  marketingAllowed: boolean;
  forceRefresh?: boolean;
}) {
  const requestedPurposeIsFenced =
    (options.analyticsFenced && options.analyticsAllowed) ||
    (options.marketingFenced && options.marketingAllowed);
  return (
    (options.analyticsAllowed || options.marketingAllowed) &&
    (options.forceRefresh ||
      !options.transportVerified ||
      requestedPurposeIsFenced)
  );
}

export function isFirstPartyCookieProbeResultCurrent(options: {
  capturedGeneration: number;
  currentGeneration: number;
  targetCurrent: boolean;
}) {
  return (
    options.targetCurrent &&
    options.capturedGeneration === options.currentGeneration
  );
}

export function hasFirstPartyConsentFenceSignals(options: {
  serverFenced: boolean;
  revokePending: boolean;
  transitionHistory: boolean;
  transitionBlocked: boolean;
}) {
  return (
    options.serverFenced ||
    options.revokePending ||
    options.transitionHistory ||
    options.transitionBlocked
  );
}

export function resolveFirstPartyConsentRevisionForDelivery(
  revision: FirstPartyConsentRevision | null,
  transitionBlocked: boolean,
): FirstPartyConsentRevision | null {
  // A bounded-history overflow must never reuse an older capability for a new
  // restriction. The tokenless denial remains fail-closed server-side while
  // local nonessential delivery stays fenced until an ACK frees a slot.
  return transitionBlocked ||
    !revision ||
    !isFirstPartyConsentRevision(revision)
    ? null
    : revision;
}

export function hasFullFirstPartyPurposeGrant(
  decisions: FirstPartyConsentPurposeDecisions,
) {
  return (Object.values(decisions) as boolean[]).every(Boolean);
}

export function isFreshFirstPartyConsentTransition(
  transition: Partial<FirstPartyConsentTransition> | undefined,
  nowMs = Date.now(),
): transition is FirstPartyConsentTransition {
  return Boolean(
    transition?.transitionVersion === 1 &&
    typeof transition.occurredAt === "number" &&
    Number.isSafeInteger(transition.occurredAt) &&
    transition.occurredAt <= nowMs &&
    transition.occurredAt >= nowMs - TRANSITION_MAX_AGE_MS,
  );
}

/**
 * Build the complete, bounded authorization claim required by the server for
 * first-party cookie issuance. Opaque CMP fields and non-boolean categories
 * never cross this boundary.
 */
export function buildFirstPartyCookieIssueRequest(options: {
  siteKey: string;
  consent: ConsentSnapshot;
  policy: FirstPartyCookieIssuePolicy;
}): FirstPartyCookieIssueRequest | null {
  const siteKey = options.siteKey.trim();
  const consent = normalizeSharedConsentSnapshot(options.consent);
  if (
    !siteKey ||
    !consent ||
    consent.state !== "granted" ||
    consent.gpc ||
    consent.dnt
  ) {
    return null;
  }
  return {
    siteKey,
    action: "issue",
    consent,
    policy: boundedPolicy(options.policy),
  };
}

export function buildFirstPartyCookieProbeRequest(
  siteKeyValue: string,
): FirstPartyCookieProbeRequest | null {
  const siteKey = siteKeyValue.trim();
  return siteKey ? { siteKey, action: "probe" } : null;
}

export function classifyFirstPartyCookieProbeResult(result: {
  ok: boolean;
  status?: number;
  responseBody?: unknown;
}): {
  state: "unverified" | "verified_clear" | "verified_fenced";
  unresolvedPurposes: FirstPartyConsentPurpose[];
} {
  if (result.status === 428) {
    const body =
      result.responseBody &&
      typeof result.responseBody === "object" &&
      !Array.isArray(result.responseBody)
        ? (result.responseBody as { unresolvedPurposes?: unknown })
        : undefined;
    const rawPurposes = Array.isArray(body?.unresolvedPurposes)
      ? body.unresolvedPurposes
      : null;
    const unresolvedPurposes = rawPurposes
      ? Array.from(
          new Set(
            rawPurposes.filter(
              (purpose): purpose is FirstPartyConsentPurpose =>
                typeof purpose === "string" &&
                FIRST_PARTY_CONSENT_PURPOSES.includes(
                  purpose as FirstPartyConsentPurpose,
                ),
            ),
          ),
        ).slice(0, FIRST_PARTY_CONSENT_PURPOSES.length)
      : [...FIRST_PARTY_CONSENT_PURPOSES];
    return {
      state: "verified_fenced",
      // A malformed or empty 428 cannot mean "no fence"; that is 204.
      unresolvedPurposes:
        unresolvedPurposes.length > 0
          ? unresolvedPurposes
          : [...FIRST_PARTY_CONSENT_PURPOSES],
    };
  }
  return {
    state: result.ok ? "verified_clear" : "unverified",
    unresolvedPurposes: [],
  };
}

export function buildFirstPartyCookieRevokeRequest(options: {
  siteKey: string;
  transitionToken: string;
  occurredAt: number;
  consent: ConsentSnapshot;
  policy: FirstPartyCookieIssuePolicy;
}): FirstPartyCookieRevokeRequest | null {
  const siteKey = options.siteKey.trim();
  const consent = normalizeSharedConsentSnapshot(options.consent);
  if (
    !siteKey ||
    !consent ||
    !isFirstPartyConsentTransitionToken(options.transitionToken) ||
    !Number.isSafeInteger(options.occurredAt) ||
    options.occurredAt <= 0
  ) {
    return null;
  }
  return {
    siteKey,
    action: "revoke",
    transitionToken: options.transitionToken,
    occurredAt: options.occurredAt,
    consent,
    policy: boundedPolicy(options.policy),
  };
}

export function buildFirstPartyCookieExpireRequest(
  siteKeyValue: string,
): FirstPartyCookieExpireRequest | null {
  const siteKey = siteKeyValue.trim();
  return siteKey ? { siteKey, action: "expire" } : null;
}

export function buildFirstPartyCookieRegrantRequest(options: {
  siteKey: string;
  transitionToken: string;
  transitionTokens: readonly string[];
  transition: Partial<FirstPartyConsentTransition> | undefined;
  consent: ConsentSnapshot;
  policy: FirstPartyCookieIssuePolicy;
  nowMs?: number;
}): FirstPartyCookieRegrantRequest | null {
  const siteKey = options.siteKey.trim();
  const consent = normalizeSharedConsentSnapshot(options.consent);
  if (
    !siteKey ||
    !consent ||
    consent.state !== "granted" ||
    consent.gpc ||
    consent.dnt ||
    !isFirstPartyConsentTransitionToken(options.transitionToken) ||
    options.transitionTokens.length === 0 ||
    options.transitionTokens.length >
      MAX_FIRST_PARTY_CONSENT_REVISION_HISTORY ||
    options.transitionTokens.some(
      (token) => !isFirstPartyConsentTransitionToken(token),
    ) ||
    !options.transitionTokens.includes(options.transitionToken) ||
    new Set(options.transitionTokens).size !==
      options.transitionTokens.length ||
    !isFreshFirstPartyConsentTransition(
      options.transition,
      options.nowMs ?? Date.now(),
    )
  ) {
    return null;
  }
  return {
    siteKey,
    action: "regrant",
    transitionVersion: 1,
    transitionToken: options.transitionToken,
    transitionTokens: [...options.transitionTokens],
    occurredAt: options.transition.occurredAt,
    consent,
    policy: boundedPolicy(options.policy),
  };
}

export function isFirstPartyCookieRegrantRequestCurrent(
  staged: FirstPartyCookieRegrantRequest,
  current: FirstPartyCookieRegrantRequest | null,
) {
  return current !== null && JSON.stringify(staged) === JSON.stringify(current);
}

export function regrantFirstPartyConsent(options: {
  url: string;
  buildCurrentRequest: () => FirstPartyCookieRegrantRequest | null;
  post: (
    url: string,
    payload: FirstPartyCookieRegrantRequest,
    options: { beforeAttempt: () => boolean },
  ) => Promise<boolean>;
}) {
  const request = options.buildCurrentRequest();
  if (!request) return Promise.resolve(false);
  return options.post(options.url, request, {
    beforeAttempt: () =>
      isFirstPartyCookieRegrantRequestCurrent(
        request,
        options.buildCurrentRequest(),
      ),
  });
}

export function isFirstPartyCookieIssueRequestCurrent(
  staged: FirstPartyCookieIssueRequest,
  current: FirstPartyCookieIssueRequest | null,
) {
  return current !== null && JSON.stringify(staged) === JSON.stringify(current);
}

export function issueFirstPartyCookie(options: {
  url: string;
  buildCurrentRequest: () => FirstPartyCookieIssueRequest | null;
  isAllowed: () => boolean;
  post: (
    url: string,
    payload: FirstPartyCookieIssueRequest,
    options: { beforeAttempt: () => boolean },
  ) => Promise<boolean>;
}) {
  const request = options.buildCurrentRequest();
  if (!request || !options.isAllowed()) return Promise.resolve(false);
  return options.post(options.url, request, {
    beforeAttempt: () =>
      options.isAllowed() &&
      isFirstPartyCookieIssueRequestCurrent(
        request,
        options.buildCurrentRequest(),
      ),
  });
}

export type FirstPartyCookieLifecycle = {
  issue: (operation: () => Promise<boolean>) => Promise<boolean>;
  revoke: (options?: {
    clearBrowserIds?: boolean;
    operation?: () => Promise<boolean>;
  }) => Promise<boolean>;
};

export type FirstPartyCookieTarget = {
  apiBase: string;
  siteKey: string;
};

export type FirstPartyCookieRevocationCoordinator = {
  issue: (
    target: FirstPartyCookieTarget,
    operation: () => Promise<boolean>,
  ) => Promise<boolean>;
  revoke: (
    target: FirstPartyCookieTarget,
    options?: {
      clearBrowserIds?: boolean;
      operation?: () => Promise<boolean>;
    },
  ) => Promise<boolean>;
  scheduleRevoke: (
    target: FirstPartyCookieTarget,
    options?: {
      clearBrowserIds?: boolean;
      operation?: () => Promise<boolean>;
    },
  ) => void;
  retryPending: () => void;
  isPending: (target: FirstPartyCookieTarget) => boolean;
  pendingTargetKeys: () => ReadonlySet<string>;
};

export function resolveFirstPartyIdentityPolicy(options: {
  identityConsentAllowed: boolean;
  firstPartyCookiesEnabled: boolean;
  cookieTransportVerified: boolean;
  killSwitch: boolean;
}) {
  const createBrowserIds =
    options.identityConsentAllowed &&
    options.cookieTransportVerified &&
    !options.killSwitch;
  return {
    createBrowserIds,
    issueServerCookie: createBrowserIds && options.firstPartyCookiesEnabled,
  };
}

export function shouldRevokeFirstPartyCookie(options: {
  siteKey: string | undefined;
  identityAllowed: boolean;
  identityRevoked: boolean;
}) {
  return (
    Boolean(options.siteKey) &&
    (options.identityRevoked || !options.identityAllowed)
  );
}

export function isFirstPartyCookiePluginEnabled(
  configDisabled: readonly string[] = [],
  flagDisabled: readonly string[] = [],
) {
  return (
    !configDisabled.includes("firstparty") &&
    !flagDisabled.includes("firstparty")
  );
}

/**
 * One runtime-wide ordering fence for first-party cookie operations. Plugin
 * instances can be unloaded/recreated, but an older revoke must always settle
 * before a later explicit regrant is allowed to issue again.
 */
export function createFirstPartyCookieLifecycle(options: {
  clearIds: () => void;
  revoke: () => Promise<boolean>;
}): FirstPartyCookieLifecycle {
  let tail: Promise<unknown> = Promise.resolve();
  const enqueue = <T>(operation: () => Promise<T>) => {
    const result = tail.catch(() => {}).then(operation);
    tail = result;
    return result;
  };

  return {
    issue: (operation) => enqueue(operation),
    revoke: (revokeOptions = {}) => {
      if (revokeOptions.clearBrowserIds !== false) options.clearIds();
      return enqueue(revokeOptions.operation ?? options.revoke);
    },
  };
}

/**
 * Coordinates cookie revocation independently for each captured ingest
 * target. A retired or unreachable origin can therefore withhold only the
 * envelopes that referenced it; it cannot head-of-line block a current-origin
 * denial or a new envelope that explicitly disables first-party cookies.
 */
export function createFirstPartyCookieRevocationCoordinator(options: {
  clearIds: () => void;
  keyOf: (target: FirstPartyCookieTarget) => string;
  revoke: (target: FirstPartyCookieTarget) => Promise<boolean>;
  sharedPending?: {
    get: (target: FirstPartyCookieTarget) => boolean;
    set: (target: FirstPartyCookieTarget) => void;
    clear: (target: FirstPartyCookieTarget) => void;
  };
}): FirstPartyCookieRevocationCoordinator {
  const lifecycles = new Map<string, FirstPartyCookieLifecycle>();
  const pending = new Map<
    string,
    {
      target: FirstPartyCookieTarget;
      operation?: () => Promise<boolean>;
      generation: number;
    }
  >();
  const inFlight = new Map<string, Promise<boolean>>();
  let nextGeneration = 0;

  const lifecycleFor = (target: FirstPartyCookieTarget) => {
    const key = options.keyOf(target);
    const existing = lifecycles.get(key);
    if (existing) return existing;
    const lifecycle = createFirstPartyCookieLifecycle({
      clearIds: () => {},
      revoke: () => options.revoke(target),
    });
    lifecycles.set(key, lifecycle);
    return lifecycle;
  };

  const startPending = (target: FirstPartyCookieTarget): Promise<boolean> => {
    const key = options.keyOf(target);
    const existing = inFlight.get(key);
    if (existing) return existing;

    let lastAttemptedGeneration = -1;
    const attempt = (async () => {
      while (true) {
        const staged = pending.get(key);
        if (!staged) return true;
        lastAttemptedGeneration = staged.generation;
        let confirmed = false;
        try {
          confirmed = await lifecycleFor(staged.target).revoke({
            clearBrowserIds: false,
            operation: staged.operation,
          });
        } catch {
          confirmed = false;
        }

        const current = pending.get(key);
        if (current?.generation !== staged.generation) {
          // A more recent restriction arrived while this exact revision was
          // being acknowledged. Keep the shared fence raised and immediately
          // handshake the newest operation, even if the older one failed.
          continue;
        }
        if (!confirmed) return false;

        pending.delete(key);
        options.sharedPending?.clear(staged.target);
        return true;
      }
    })().finally(() => {
      if (inFlight.get(key) !== attempt) return;
      inFlight.delete(key);
      const current = pending.get(key);
      // A revoke can be queued synchronously by a shared-pending adapter while
      // the prior completion is clearing its marker. Do not strand it behind
      // the promise that has just settled; ordinary failed retries remain
      // explicitly pending until retryPending/issue schedules them again.
      if (current && current.generation !== lastAttemptedGeneration) {
        void startPending(current.target);
      }
    });
    inFlight.set(key, attempt);
    return attempt;
  };

  const revoke = (
    target: FirstPartyCookieTarget,
    revokeOptions: {
      clearBrowserIds?: boolean;
      operation?: () => Promise<boolean>;
    } = {},
  ) => {
    const key = options.keyOf(target);
    if (revokeOptions.clearBrowserIds !== false) options.clearIds();
    pending.set(key, {
      target,
      operation: revokeOptions.operation,
      generation: ++nextGeneration,
    });
    options.sharedPending?.set(target);
    return startPending(target);
  };

  const scheduleRevoke = (
    target: FirstPartyCookieTarget,
    revokeOptions: {
      clearBrowserIds?: boolean;
      operation?: () => Promise<boolean>;
    } = {},
  ) => {
    void revoke(target, revokeOptions);
  };

  return {
    issue: async (target, operation) => {
      const key = options.keyOf(target);
      const localPending = pending.get(key);
      if (localPending || options.sharedPending?.get(target)) {
        // Never reuse or recreate a server identifier until the prior cookie
        // has been authoritatively expired for this exact target.
        const confirmed = localPending
          ? await startPending(localPending.target)
          : await revoke(target, { clearBrowserIds: false });
        if (!confirmed) return false;
      }
      return lifecycleFor(target).issue(operation);
    },
    revoke,
    scheduleRevoke,
    retryPending: () => {
      for (const entry of Array.from(pending.values())) {
        void startPending(entry.target);
      }
    },
    isPending: (target) =>
      pending.has(options.keyOf(target)) ||
      options.sharedPending?.get(target) === true,
    pendingTargetKeys: () => new Set(pending.keys()),
  };
}

/** Stable, non-sensitive localStorage key for a public ingest/site target. */
export function firstPartyCookieRevocationStorageKey(
  target: FirstPartyCookieTarget,
) {
  const input = `${target.apiBase.replace(/\/+$/, "")}\n${target.siteKey}`;
  // Both values are already public browser configuration. Encoding the full
  // bounded target avoids hash collisions that could let one origin clear a
  // different origin's privacy fence.
  return `prospix_fp_revoke_pending_${encodeURIComponent(input)}`;
}

/**
 * Expire the server-managed HttpOnly first-party identifier. This endpoint is
 * deliberately independent of ingest, queues, and database availability so a
 * local opt-out can always attempt an idempotent cookie revocation.
 */
export function revokeFirstPartyCookie(apiBase: string, siteKey: string) {
  const url = `${apiBase.replace(/\/+$/, "")}/pxl/firstparty`;
  return postWithRetryResult(url, { siteKey, action: "expire" });
}
